/** Ordered for the hero-page chip picker — kept here so UI + backend agree. */
export const VISUALS_CAPABILITIES = [
    'text-to-image',
    'image-to-image',
    'text-to-video',
    'image-to-video',
    'edit-image',
    'edit-video',
    'audio',
];
export function capabilityLabel(c) {
    switch (c) {
        case 'text-to-image':
            return 'Text → image';
        case 'image-to-image':
            return 'Image → image';
        case 'text-to-video':
            return 'Text → video';
        case 'image-to-video':
            return 'Image → video';
        case 'edit-image':
            return 'Edit image';
        case 'edit-video':
            return 'Edit video';
        case 'audio':
            return 'Audio';
    }
}
/**
 * Curated catalog. Admin enables a subset in Settings; the hero only shows
 * enabled models filtered by the selected capability chip.
 *
 * Order within a capability = order in the picker. Aspect ratios mirror each
 * model's accepted strings — empty array means the provider doesn't accept
 * the knob (we just don't send aspect_ratio).
 *
 * Adding a new fal.ai model is free — the queue API is uniform. Adding a new
 * kie.ai model requires also wiring it into the kieEndpointFor() adapter
 * in visuals-providers.service.ts, since each kie family has its own
 * submit/poll endpoint shape.
 */
export const VISUALS_MODELS = [
    // ======================== fal.ai · text-to-image ========================
    {
        id: 'fal-ai/flux-pro/v1.1-ultra',
        provider: 'fal',
        label: 'Flux Pro 1.1 Ultra',
        description: 'Top-tier photoreal generation up to 2K.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16', '9:21'],
        requiresInput: false,
        approxSeconds: 15,
    },
    {
        id: 'fal-ai/flux-pro/v1.1',
        provider: 'fal',
        label: 'Flux Pro 1.1',
        description: 'Faster, cheaper Flux Pro tier.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16', '9:21'],
        requiresInput: false,
        approxSeconds: 10,
    },
    {
        id: 'fal-ai/flux/dev',
        provider: 'fal',
        label: 'Flux.1 [dev]',
        description: 'Open-weights Flux dev. Good quality for general use.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 8,
    },
    {
        id: 'fal-ai/flux/schnell',
        provider: 'fal',
        label: 'Flux.1 [schnell]',
        description: 'Sub-second image generation. Lower fidelity.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 3,
    },
    {
        id: 'fal-ai/ideogram/v3',
        provider: 'fal',
        label: 'Ideogram v3',
        description: 'Strongest in-image typography and branding.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 12,
    },
    {
        id: 'fal-ai/recraft-v3',
        provider: 'fal',
        label: 'Recraft v3',
        description: 'Vector + raster generation with style references.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 12,
    },
    {
        id: 'fal-ai/bytedance/seedream/v4/text-to-image',
        provider: 'fal',
        label: 'Seedream v4',
        description: "ByteDance's unified generation + editing model.",
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16', '9:21'],
        requiresInput: false,
        approxSeconds: 12,
    },
    {
        id: 'fal-ai/imagen4/preview',
        provider: 'fal',
        label: 'Imagen 4 (preview)',
        description: "Google's Imagen 4. Strong photoreal + prompt adherence.",
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 14,
    },
    // ======================== fal.ai · image-to-image ========================
    // --- Reframe: change aspect ratio without re-cropping the subject. -------
    // These EXTEND the canvas (outpaint) instead of cutting into the image, so a
    // wide hero can become 1:1 or 9:16 for social without losing the product,
    // logo or face at the edges. Neither takes a prompt — they resize a canvas
    // rather than generate from a description.
    {
        id: 'fal-ai/image-editing/reframe',
        provider: 'fal',
        label: 'Reframe (aspect ratio)',
        description: 'Change an image to a new aspect ratio — extends the canvas instead of cropping the subject.',
        capability: 'image-to-image',
        outputKind: 'image',
        // fal's own enum, in its documented order.
        aspectRatios: ['21:9', '16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16', '9:21'],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 15,
        fal: { promptless: true },
    },
    {
        id: 'fal-ai/ideogram/v3/reframe',
        provider: 'fal',
        label: 'Ideogram V3 Reframe',
        description: 'Extends an image to a new size while preserving the original. Slower, strongest subject fidelity.',
        capability: 'image-to-image',
        outputKind: 'image',
        // Ideogram takes `image_size` PRESET NAMES, not ratio strings — hence the
        // aspectField override. Values are fal's documented preset list.
        aspectRatios: [
            'square_hd',
            'square',
            'portrait_4_3',
            'portrait_16_9',
            'landscape_4_3',
            'landscape_16_9',
        ],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 25,
        fal: { aspectField: 'image_size', promptless: true },
    },
    {
        id: 'fal-ai/flux/dev/image-to-image',
        provider: 'fal',
        label: 'Flux.1 [dev] (image→image)',
        description: 'Restyle / transform an existing image with a prompt.',
        capability: 'image-to-image',
        outputKind: 'image',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 10,
    },
    {
        id: 'fal-ai/recraft-v3/image-to-image',
        provider: 'fal',
        label: 'Recraft v3 (image→image)',
        description: 'Style-transfer an input image into a new render.',
        capability: 'image-to-image',
        outputKind: 'image',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 12,
    },
    // ---- fal.ai · Nano Banana (text-to-image variants live here) ----
    // The Nano Banana model has both generation + edit endpoints on fal.ai —
    // separate ids per mode, unlike kie which unifies them on one model id.
    {
        id: 'fal-ai/nano-banana',
        provider: 'fal',
        label: 'Nano Banana (text→image)',
        description: 'Gemini 2.5 Flash Image — fast, high-fidelity text-to-image.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 14,
    },
    {
        id: 'fal-ai/nano-banana/pro',
        provider: 'fal',
        label: 'Nano Banana Pro (text→image)',
        description: 'Pro tier — strongest text rendering + reference grounding.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 18,
    },
    // ======================== fal.ai · edit-image ========================
    {
        id: 'fal-ai/nano-banana/edit',
        provider: 'fal',
        label: 'Nano Banana Edit',
        description: 'Natural-language image editor (Gemini 2.5 Flash Image).',
        capability: 'edit-image',
        outputKind: 'image',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 18,
    },
    {
        id: 'fal-ai/nano-banana/pro/edit',
        provider: 'fal',
        label: 'Nano Banana Pro Edit',
        description: 'Pro tier image edit — multi-reference + identity preservation.',
        capability: 'edit-image',
        outputKind: 'image',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 22,
    },
    {
        id: 'fal-ai/flux-pro/kontext',
        provider: 'fal',
        label: 'Flux Pro Kontext',
        description: 'Surgical prompt-guided edits while preserving identity.',
        capability: 'edit-image',
        outputKind: 'image',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 20,
    },
    {
        id: 'fal-ai/bytedance/seedream/v4/edit',
        provider: 'fal',
        label: 'Seedream v4 Edit',
        description: 'ByteDance unified editor — multi-image references too.',
        capability: 'edit-image',
        outputKind: 'image',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 15,
    },
    {
        id: 'fal-ai/ideogram/v3/edit',
        provider: 'fal',
        label: 'Ideogram v3 Edit',
        description: 'Inpaint with strong typography preservation.',
        capability: 'edit-image',
        outputKind: 'image',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 14,
    },
    // ======================== fal.ai · text-to-video ========================
    {
        id: 'fal-ai/veo3.1',
        provider: 'fal',
        label: 'Veo 3.1',
        description: "Google's top text-to-video model with native audio.",
        capability: 'text-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '9:16'],
        requiresInput: false,
        approxSeconds: 90,
    },
    {
        id: 'fal-ai/veo3.1/fast',
        provider: 'fal',
        label: 'Veo 3.1 Fast',
        description: 'Veo 3.1 at lower cost / latency — still has audio.',
        capability: 'text-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '9:16'],
        requiresInput: false,
        approxSeconds: 45,
    },
    {
        id: 'fal-ai/kling-video/v2.5-turbo/pro/text-to-video',
        provider: 'fal',
        label: 'Kling 2.5 Turbo Pro (T2V)',
        description: 'Cinematic motion, accurate prompt following.',
        capability: 'text-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '9:16', '1:1'],
        requiresInput: false,
        approxSeconds: 70,
    },
    {
        id: 'fal-ai/sora-2-text-to-video',
        provider: 'fal',
        label: 'Sora 2',
        description: "OpenAI Sora 2 — narrative video with audio.",
        capability: 'text-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '9:16'],
        requiresInput: false,
        approxSeconds: 120,
    },
    {
        id: 'fal-ai/luma-dream-machine/ray-2',
        provider: 'fal',
        label: 'Luma Ray 2',
        description: 'Luma Dream Machine Ray 2 — strong motion fidelity.',
        capability: 'text-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
        requiresInput: false,
        approxSeconds: 60,
    },
    // ======================== fal.ai · image-to-video ========================
    {
        id: 'fal-ai/kling-video/v2.5-turbo/pro/image-to-video',
        provider: 'fal',
        label: 'Kling 2.5 Turbo Pro (I2V)',
        description: 'Animate a still image with cinematic motion.',
        capability: 'image-to-video',
        outputKind: 'video',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 75,
    },
    {
        id: 'fal-ai/veo3.1/image-to-video',
        provider: 'fal',
        label: 'Veo 3.1 (I2V)',
        description: 'Veo 3.1 driven by a starting frame.',
        capability: 'image-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '9:16'],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 90,
    },
    {
        id: 'fal-ai/luma-dream-machine/ray-2/image-to-video',
        provider: 'fal',
        label: 'Luma Ray 2 (I2V)',
        description: 'Luma Ray 2 driven by a starting frame.',
        capability: 'image-to-video',
        outputKind: 'video',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 60,
    },
    {
        id: 'fal-ai/wan-pro/image-to-video',
        provider: 'fal',
        label: 'Wan Pro (I2V)',
        description: 'Wan Pro — fast image-to-video.',
        capability: 'image-to-video',
        outputKind: 'video',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 50,
    },
    // ======================== fal.ai · edit-video ========================
    {
        id: 'fal-ai/runway-aleph',
        provider: 'fal',
        label: 'Runway Aleph (video edit)',
        description: 'Prompt-driven edits on an existing video clip.',
        capability: 'edit-video',
        outputKind: 'video',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'video',
        approxSeconds: 120,
    },
    {
        id: 'fal-ai/luma-dream-machine/ray-2/modify',
        provider: 'fal',
        label: 'Luma Ray 2 Modify',
        description: 'Restyle / transform a clip with a text prompt.',
        capability: 'edit-video',
        outputKind: 'video',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'video',
        approxSeconds: 90,
    },
    // ======================== kie.ai · image ========================
    //
    // Nano Banana 2 and Nano Banana Pro are unified models — the SAME kie
    // model id drives both text-to-image and edit modes; the only difference
    // is whether we send `image_input`. We surface separate catalog entries
    // per capability so the hero chips work cleanly.
    {
        id: 'kie/nano-banana-pro',
        provider: 'kie',
        label: 'Nano Banana Pro (text→image)',
        description: 'Gemini 3 Pro / 2.5 Flash Image — top-tier text-to-image.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 18,
        kie: { dispatch: 'unified', modelField: 'nano-banana-pro' },
    },
    {
        id: 'kie/nano-banana-pro/edit',
        provider: 'kie',
        label: 'Nano Banana Pro (edit)',
        description: 'Same model in image-edit mode — accepts up to 8 reference images.',
        capability: 'edit-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 20,
        kie: { dispatch: 'unified', modelField: 'nano-banana-pro', inputUrlField: 'image_input', inputUrlIsArray: true },
    },
    {
        id: 'kie/nano-banana-2',
        provider: 'kie',
        label: 'Nano Banana 2 (text→image)',
        description: 'Gemini 3.1 Flash Image — fast, high-fidelity generation.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 14,
        kie: { dispatch: 'unified', modelField: 'nano-banana-2' },
    },
    {
        id: 'kie/nano-banana-2/edit',
        provider: 'kie',
        label: 'Nano Banana 2 (edit)',
        description: 'Same model in image-edit mode — up to 14 reference images.',
        capability: 'edit-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 16,
        kie: { dispatch: 'unified', modelField: 'nano-banana-2', inputUrlField: 'image_input', inputUrlIsArray: true },
    },
    {
        id: 'kie/flux-kontext-pro',
        provider: 'kie',
        label: 'Flux Kontext Pro (kie.ai)',
        description: 'Context-aware edits that preserve identity.',
        capability: 'edit-image',
        outputKind: 'image',
        aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 18,
        kie: { dispatch: 'legacy', modelField: 'flux/kontext', inputUrlField: 'inputImage', aspectField: 'aspectRatio' },
    },
    {
        id: 'kie/gpt-image-2',
        provider: 'kie',
        label: 'GPT Image 2 (kie.ai)',
        description: "OpenAI's GPT Image 2 — text-to-image with strong rendering.",
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 20,
        kie: { dispatch: 'unified', modelField: 'gpt-image-2' },
    },
    {
        id: 'kie/gpt-image-2/i2i',
        provider: 'kie',
        label: 'GPT Image 2 (image→image)',
        description: 'GPT Image 2 image-editing variant.',
        capability: 'image-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 20,
        kie: { dispatch: 'unified', modelField: 'gpt-image-2-image-to-image', inputUrlField: 'input_urls', inputUrlIsArray: true },
    },
    {
        id: 'kie/midjourney',
        provider: 'kie',
        label: 'Midjourney (kie.ai)',
        description: 'Midjourney through kie — stylized, painterly imagery.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 30,
        kie: { dispatch: 'unified', modelField: 'midjourney' },
    },
    {
        id: 'kie/imagen4',
        provider: 'kie',
        label: 'Imagen 4 (kie.ai)',
        description: "Google's Imagen 4 — strong prompt adherence.",
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 15,
        kie: { dispatch: 'unified', modelField: 'imagen4' },
    },
    {
        id: 'kie/ideogram-v3',
        provider: 'kie',
        label: 'Ideogram v3 (kie.ai)',
        description: 'Strong typography + character consistency.',
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 14,
        kie: { dispatch: 'unified', modelField: 'ideogram-v3' },
    },
    {
        id: 'kie/seedream-v4',
        provider: 'kie',
        label: 'Seedream v4 (kie.ai)',
        description: "ByteDance Seedream v4 unified generation.",
        capability: 'text-to-image',
        outputKind: 'image',
        aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16', '9:21'],
        requiresInput: false,
        approxSeconds: 14,
        kie: { dispatch: 'unified', modelField: 'bytedance/seedream' },
    },
    // ======================== kie.ai · video ========================
    {
        id: 'kie/veo3',
        provider: 'kie',
        label: 'Veo 3.1 (kie.ai)',
        description: "Google's Veo 3.1 — text-to-video with audio.",
        capability: 'text-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '9:16'],
        requiresInput: false,
        approxSeconds: 90,
        kie: { dispatch: 'unified', modelField: 'veo3', inputUrlField: 'imageUrls', inputUrlIsArray: true },
    },
    {
        id: 'kie/veo3-fast',
        provider: 'kie',
        label: 'Veo 3.1 Fast (kie.ai)',
        description: 'Cheaper / faster Veo 3.1 tier — still has audio.',
        capability: 'text-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '9:16'],
        requiresInput: false,
        approxSeconds: 45,
        kie: { dispatch: 'unified', modelField: 'veo3_fast', inputUrlField: 'imageUrls', inputUrlIsArray: true },
    },
    {
        id: 'kie/sora-2',
        provider: 'kie',
        label: 'Sora 2 (kie.ai)',
        description: "OpenAI's Sora 2 routed through kie.",
        capability: 'text-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '9:16'],
        requiresInput: false,
        approxSeconds: 120,
        kie: { dispatch: 'unified', modelField: 'sora-2-text-to-video' },
    },
    {
        id: 'kie/kling-v2.5',
        provider: 'kie',
        label: 'Kling 2.5 (kie.ai)',
        description: 'Cinematic motion, strong prompt following.',
        capability: 'text-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '9:16', '1:1'],
        requiresInput: false,
        approxSeconds: 70,
        kie: { dispatch: 'unified', modelField: 'kling-v2.5' },
    },
    {
        id: 'kie/kling-v2.5/i2v',
        provider: 'kie',
        label: 'Kling 2.5 (image→video)',
        description: 'Animate a still image with cinematic motion.',
        capability: 'image-to-video',
        outputKind: 'video',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 70,
        kie: { dispatch: 'unified', modelField: 'kling-v2.5-image-to-video', inputUrlField: 'image_url' },
    },
    {
        id: 'kie/runway',
        provider: 'kie',
        label: 'Runway Gen-3 (kie.ai)',
        description: 'Runway video generator — text or image input.',
        capability: 'text-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
        requiresInput: false,
        approxSeconds: 60,
        kie: { dispatch: 'legacy', modelField: 'runway' },
    },
    {
        id: 'kie/runway-aleph',
        provider: 'kie',
        label: 'Runway Aleph (video edit)',
        description: 'Prompt-driven edits on an existing video clip.',
        capability: 'edit-video',
        outputKind: 'video',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'video',
        approxSeconds: 120,
        kie: { dispatch: 'unified', modelField: 'runway-aleph', inputUrlField: 'image_url' },
    },
    {
        id: 'kie/hailuo',
        provider: 'kie',
        label: 'Hailuo (kie.ai)',
        description: 'MiniMax Hailuo video — multiple visual styles.',
        capability: 'text-to-video',
        outputKind: 'video',
        aspectRatios: ['16:9', '9:16'],
        requiresInput: false,
        approxSeconds: 70,
        kie: { dispatch: 'unified', modelField: 'hailuo' },
    },
    {
        id: 'kie/wan-pro/i2v',
        provider: 'kie',
        label: 'Wan Pro (image→video)',
        description: 'Wan Pro — fast cinematic image-to-video.',
        capability: 'image-to-video',
        outputKind: 'video',
        aspectRatios: [],
        requiresInput: true,
        inputKind: 'image',
        approxSeconds: 60,
        kie: { dispatch: 'unified', modelField: 'wan-pro-image-to-video', inputUrlField: 'image_url' },
    },
    // ======================== kie.ai · audio (Suno) ========================
    {
        id: 'kie/suno-v5',
        provider: 'kie',
        label: 'Suno V5 (kie.ai)',
        description: 'Latest Suno model — music generation from a prompt.',
        capability: 'audio',
        outputKind: 'audio',
        aspectRatios: [],
        requiresInput: false,
        approxSeconds: 60,
        kie: { dispatch: 'suno', modelField: 'V5' },
    },
    {
        id: 'kie/suno-v4.5plus',
        provider: 'kie',
        label: 'Suno V4.5 Plus (kie.ai)',
        description: 'Suno V4.5+ — strong vocals + structure.',
        capability: 'audio',
        outputKind: 'audio',
        aspectRatios: [],
        requiresInput: false,
        approxSeconds: 60,
        kie: { dispatch: 'suno', modelField: 'V4_5PLUS' },
    },
];
export function findVisualsModel(id) {
    return VISUALS_MODELS.find((m) => m.id === id);
}
/**
 * Build the JSON Schema an agent should emit when running in `direct_provider`
 * dispatch mode against this model. Mirrors the four user-controllable inputs
 * the visuals dispatcher accepts (prompt, aspectRatio, inputAssetUrl,
 * extraParams). Used by the agent service to populate Agent.outputJsonSchema
 * so the rest of the proposal pipeline keeps working unchanged.
 */
export function buildDirectProviderOutputSchema(model) {
    const properties = {
        prompt: {
            type: 'string',
            minLength: 1,
            description: `Complete prompt to send to ${model.label}. Be specific and concrete.`,
        },
    };
    const required = ['prompt'];
    if (model.aspectRatios.length > 0) {
        properties.aspectRatio = {
            type: 'string',
            enum: model.aspectRatios,
            description: 'Output aspect ratio.',
        };
        required.push('aspectRatio');
    }
    if (model.requiresInput) {
        const kindLabel = model.inputKind ?? 'image';
        properties.inputAssetUrl = {
            type: 'string',
            format: 'uri',
            description: `Publicly reachable URL of the input ${kindLabel} (Drive direct link, R2 public URL, etc.).`,
        };
        required.push('inputAssetUrl');
    }
    return {
        $schema: 'http://json-schema.org/draft-07/schema#',
        type: 'object',
        properties,
        required,
        additionalProperties: false,
    };
}
/**
 * Build a full, ready-to-use system prompt for a `direct_provider` agent.
 *
 * This is the WHOLE prompt — it covers identity, the fields to collect, the
 * conversational style, and an in-chat confirmation step before the agent
 * emits the proposal JSON. The runtime engine still appends the standard
 * "Output Contract" section (JSON markers + card-based confirm/reject) and
 * any media-uploads guidance separately, so don't duplicate those here.
 *
 * Admins are free to edit the auto-filled value in the agent editor to add
 * persona/tone instructions; switching the model regenerates it.
 */
export function buildDirectProviderSystemPrompt(model) {
    const lines = [];
    lines.push(`You are an AI assistant that helps users generate ${model.outputKind}s using ${model.label} (${model.provider}, ${capabilityLabel(model.capability)}).`);
    lines.push('');
    lines.push(`Chat naturally with the user, collect the inputs the model needs, recap them, and ask the user to confirm before emitting the proposal JSON.`);
    lines.push('');
    lines.push('# What the model needs');
    lines.push(`- prompt (string, required) — the final ${model.outputKind} description. Turn the user's idea into something specific and concrete (subject, style, mood, lighting, composition).`);
    if (model.aspectRatios.length > 0) {
        lines.push(`- aspectRatio (required) — one of: ${model.aspectRatios.join(', ')}.`);
    }
    if (model.requiresInput) {
        const kindLabel = model.inputKind ?? 'image';
        lines.push(`- inputAssetUrl (required) — a publicly reachable URL of the input ${kindLabel}. The user can attach one in chat; when they do you'll see a system note like "[USER ATTACHED: ... drive_url=...]" — copy that drive_url into this field.`);
    }
    lines.push('');
    lines.push('# How to chat');
    lines.push('- Open with a short greeting and ask what they want to create. Keep it warm and concise.');
    lines.push('- Ask follow-up questions ONE AT A TIME. Never dump a form on the user.');
    lines.push("- If their idea is vague, offer two or three concrete options to pick from rather than asking open-ended questions.");
    if (model.aspectRatios.length > 0) {
        lines.push('- If they don\'t mention an aspect ratio, suggest a sensible default based on the use case and confirm.');
    }
    if (model.requiresInput) {
        const kindLabel = model.inputKind ?? 'image';
        lines.push(`- The model needs a source ${kindLabel}. If the user hasn't shared one, ask them to attach it before you can run.`);
    }
    lines.push('');
    lines.push('# Confirmation');
    lines.push('When you have everything, summarize the collected inputs back to the user in a short, scannable recap and ask: "Looks good? Reply confirm to generate, or tell me what to change."');
    lines.push('If they confirm, emit the proposal JSON (see the Output Contract section below). If they ask for changes, update the relevant field, recap again, and re-confirm before emitting.');
    lines.push('');
    lines.push(`After you emit the JSON, the system handles dispatch to ${model.label} (~${model.approxSeconds}s) and renders the ${model.outputKind} inline as soon as it's ready — you don't need to do anything else.`);
    return lines.join('\n');
}
/**
 * Default system prompt baked into the singleton on first read if the admin
 * hasn't customized it. Exposed here (not just in the API) so admins can see
 * the baseline in the settings UI.
 */
export const DEFAULT_VISUALS_PROMPT_AI_SYSTEM = `You are a professional prompt engineer specialized in creating high-quality prompts for AI image and video generation models. Your job is to combine the inputs the user provides — any of an Idea, an Input prompt, and a Template prompt — into a single coherent, detailed, optimized final prompt.

Rules:
- Output ONLY the final prompt text. No preamble, explanation, quotes, or markdown.
- Preserve the user's creative intent above all else.
- Use vivid, specific, descriptive language: subject, action, environment, lighting, mood, style, composition, camera, quality modifiers.
- If a Template prompt is provided, treat it as a structural skeleton — substitute the user's idea/details into it rather than ignoring or duplicating it.
- If an Input prompt is provided, treat it as a near-final draft to polish and enrich — don't rewrite its core intent.
- If only an Idea is provided, expand it into a full prompt.
- Keep the final prompt under 400 words. Stay focused.`;
//# sourceMappingURL=visuals-generator.js.map